import { AxiosError, AxiosResponse } from 'axios';
import { useCustomMutate, useCustomQuery } from '../../../../../hooks/query';

import {
  deleteAccessArrangementFile,
  deleteAccessArrangementRequest,
  deleteAccessArrangementRequestDetails,
  getAccessArrangemenDraftDateTime,
  getAccessArrangementMedicalConditionAmendment,
  getAccessArrangementMedicalCondtition,
  getAccessArrangementRequest,
  getAccessArrangementRequestDetails,
  getAccessArrangementSelectedCondtition,
  getMedicalConditionAmendment,
  getRequestAcknoledgment,
  getRequestSummary,
  getReviewAmendmentApplicationData,
  getReviewApplicationData,
  getSummaryPdf,
  getIndividualReportFiles,
  getIndividualReportSubmittedFiles,
  postAccessArrangementCoordinatorNewApplication,
  postAccessArrangementDraft,
  postAccessArrangementModifySelectedMedicalConditions,
  postAccessArrangementNewApplication,
  postAccessArrangementRequestDetails,
  postAccessArrangementUploadDocuments,
  putAccessArrangementRequestDetails,
  putReviewApplicationData,
} from '../api-services/api';
import {
  AccessArrangementAcknowledgementResponse,
  AccessArrangementGetReviewAmendment,
  AccessArrangementGetReviewDetails,
  AccessArrangementRequestInfoModel,
  AccessArrangmentGetMedicalConditionsResponse,
  AccessArrangmentGetSelectedResponse,
  AccessArrangmentGetSummaryDetails,
  AccessArrangmentGetSummaryPDF,
  AccessArrangmentPostCreateApplication,
  AccessArrangmentPostModifyDraftRequestData,
  AccessArrangmentPostUploadDocumentsRequest,
  MedicalConditionAmendment,
  UpdateAccessArrangementData,
} from '../../model';
import {
  DELETE_ACCESS_ARRANGEMENT_FILE,
  DELETE_ACCESS_ARRANGEMENT_REQUEST,
  DELETE_ACCESS_ARRANGEMENT_REQUEST_DETAILS,
  GET_ACCESS_ARRANGEMENT_DOWNLOAD_PDF,
  GET_ACCESS_ARRANGEMENT_DRAFT_DATE_TIME,
  GET_ACCESS_ARRANGEMENT_MEDICAL_CONDITIONS,
  GET_ACCESS_ARRANGEMENT_MEDICAL_CONDITIONS_AMENDMENT,
  GET_ACCESS_ARRANGEMENT_REQUEST,
  GET_ACCESS_ARRANGEMENT_REQUEST_DETAILS,
  GET_ACCESS_ARRANGEMENT_REVIEW_DETAILS_AMENDMENT,
  GET_ACCESS_ARRANGEMENT_SELECTED_CONDITIONS,
  GET_ACCESS_ARRANGEMENT_INDIVIDUAL_REPORT_FILES,
  GET_ACCESS_ARRANGEMENT_INDIVIDUAL_REPORT_SUBMITTED_FILES,
  GET_ACCESS_ARRANGEMENT_SUBMITTED_DRAFT,
  GET_ACCESS_ARRANGEMENT_SUMMARY,
  GET_MEDICAL_CONDITIONS_AMENDMENT,
  POST_ACCESS_ARRANGEMENT_MODIFY_SELECTED_MEDICAL_CONDITIONS,
  POST_ACCESS_ARRANGEMENT_NEW_APPLICATION,
  POST_ACCESS_ARRANGEMENT_POST_DRAFT_APPLICATION,
  POST_ACCESS_ARRANGEMENT_REQUEST_DETAILS,
  PUT_ACCESS_ARRANGEMENT_REQUEST_DETAILS,
  PUT_ACCESS_ARRANGEMENT_UPDATE_DRAFT,
} from './keys';
import { GenericErrorResponse } from '../../../..';
import { ApiPathParam } from '../../../../../models/SearchParamSchema';
import { AccessArrangementResultListing } from '../../../../../modules/accessArrangment/components/candidate/AccessArrangements/AccessArrangementsModels';

/**
 * to get access arrangement request
 */
export const useGetAccessArrangementRequest = ({ key }: { key: string }) =>
  useCustomQuery<AccessArrangementResultListing>([GET_ACCESS_ARRANGEMENT_REQUEST, key], () =>
    getAccessArrangementRequest(key).then((response) => response.data)
  );

/**
 * to delete access arrangement request
 */
export const useDeleteAccessArrangementRequest = (onSuccess: (res: any) => void) =>
  useCustomMutate(DELETE_ACCESS_ARRANGEMENT_REQUEST, deleteAccessArrangementRequest, {
    onSuccess,
  });

/** to get appeal exam level fees
 * */
export const usePostAppealArrangementDraft = (
  successCallback: (res: any) => void,
  failCallback: (res: any) => void
) =>
  useCustomMutate<
    AxiosResponse<AccessArrangmentPostCreateApplication>,
    AccessArrangmentPostCreateApplication,
    AxiosError<GenericErrorResponse>
  >(
    POST_ACCESS_ARRANGEMENT_POST_DRAFT_APPLICATION,
    postAccessArrangementDraft,
    {
      onSuccess: (res) => successCallback(res),
      onError: (err) => failCallback(err),
    },
    false,
    false
  );

/** to post to create new application
 * */
export const usePostAppealArrangementCreateApplication = (
  successCallback: (res: any) => void,
  failCallback: (res: any) => void
) =>
  useCustomMutate<
    AxiosResponse<AccessArrangmentPostCreateApplication>,
    {},
    AxiosError<GenericErrorResponse>
  >(
    POST_ACCESS_ARRANGEMENT_NEW_APPLICATION,
    postAccessArrangementNewApplication,
    {
      onSuccess: (res) => successCallback(res),
      onError: (err) => failCallback(err),
    },
    false,
    false
  );

export const usePostAppealArrangementCoordinatorCreateApplication = (
  successCallback: (res: any) => void,
  failCallback: (res: any) => void
) =>
  useCustomMutate<
    AxiosResponse<AccessArrangmentPostCreateApplication>,
    {},
    AxiosError<GenericErrorResponse>
  >(
    POST_ACCESS_ARRANGEMENT_NEW_APPLICATION,
    postAccessArrangementCoordinatorNewApplication,
    {
      onSuccess: (res) => successCallback(res),
      onError: (err) => failCallback(err),
    },
    false,
    false
  );

/** to get medical conditions
 * */
export const useGetAppealArrangementMedicalConditions = (
  successCallback: (res: AccessArrangmentGetMedicalConditionsResponse) => void
) =>
  useCustomQuery<AccessArrangmentGetMedicalConditionsResponse>(
    GET_ACCESS_ARRANGEMENT_MEDICAL_CONDITIONS,
    () => getAccessArrangementMedicalCondtition().then((response) => response.data),
    { onSuccess: successCallback }
  );

/** to get datetime of last saved draft (ie. when draft was last modified)
 * */
export const useGetAppealArrangementDraftDateTime = (uuid: string) =>
  useCustomQuery(GET_ACCESS_ARRANGEMENT_DRAFT_DATE_TIME, () =>
    getAccessArrangemenDraftDateTime(uuid).then((response) => response.data)
  );
/** to get selected medical conditions
 * */
export const useGetAppealArrangementSelectedConditions = (
  uuid: string,
  onSuccess: (res: AccessArrangmentGetSelectedResponse) => void
) =>
  useCustomQuery<AccessArrangmentGetSelectedResponse>(
    GET_ACCESS_ARRANGEMENT_SUBMITTED_DRAFT,
    () => getAccessArrangementSelectedCondtition(uuid).then((response) => response.data),
    {
      enabled: false,
      onSuccess,
    }
  );

/** to add medical condition and remarks to application
 * */
export const usePostAppealArrangementModifySelectedMedicalConditions = (
  successCallback: (res: any) => void,
  failCallback: (res: any) => void
) =>
  useCustomMutate<
    AxiosResponse<AccessArrangmentPostModifyDraftRequestData>,
    AccessArrangmentPostModifyDraftRequestData,
    AxiosError<GenericErrorResponse>
  >(
    POST_ACCESS_ARRANGEMENT_MODIFY_SELECTED_MEDICAL_CONDITIONS,
    postAccessArrangementModifySelectedMedicalConditions,
    {
      onSuccess: (res) => successCallback(res),
      onError: (err) => failCallback(err),
    },
    false,
    false
  );

/** to delete file from draft
 * */
export const useDeleteAppealArrangementFile = (
  successCallback?: (res: any) => void,
  failCallback?: (res: any) => void
) =>
  useCustomMutate<AxiosResponse<{}>, string, AxiosError<GenericErrorResponse>>(
    DELETE_ACCESS_ARRANGEMENT_FILE,
    deleteAccessArrangementFile,
    {
      onSuccess: successCallback,
      onError: failCallback,
    },
    false,
    false
  );

/** to get all access arrangement request details and selection options
 * */
export const useGetAccessArrangementRequestDetails = (uuid: string, paging: ApiPathParam) =>
  useCustomQuery<AccessArrangementRequestInfoModel>(GET_ACCESS_ARRANGEMENT_REQUEST_DETAILS, () =>
    getAccessArrangementRequestDetails(uuid, paging).then((res) => res.data)
  );

/** to post to create a new access arrangement request detail
 * */
export const usePostAccessArrangementRequestDetails = (
  onSuccess: (res: any) => void,
  onError: (res: any) => void
) =>
  useCustomMutate(POST_ACCESS_ARRANGEMENT_REQUEST_DETAILS, postAccessArrangementRequestDetails, {
    onSuccess,
    onError,
  });

/** to put to update the access arrangement request detail
 * */
export const usePutAccessArrangementRequestDetails = (
  onSuccess: (res: any) => void,
  onError: (res: any) => void
) =>
  useCustomMutate(PUT_ACCESS_ARRANGEMENT_REQUEST_DETAILS, putAccessArrangementRequestDetails, {
    onSuccess,
    onError,
  });

/** to delete an access arrangement request detail
 * */
export const useDeleteAccessArrangementRequestDetails = (onSuccess: (res: any) => void) =>
  useCustomMutate(
    DELETE_ACCESS_ARRANGEMENT_REQUEST_DETAILS,
    deleteAccessArrangementRequestDetails,
    {
      onSuccess,
    }
  );
/** to post to get presignUrl
 * */
export const usePostAppealArrangementUploadDocuments = (
  successCallback: (res: any) => void,
  failCallback?: (res: any) => void
) =>
  useCustomMutate<
    AxiosResponse<AccessArrangmentPostUploadDocumentsRequest>,
    AccessArrangmentPostUploadDocumentsRequest,
    AxiosError<GenericErrorResponse>
  >(
    POST_ACCESS_ARRANGEMENT_MODIFY_SELECTED_MEDICAL_CONDITIONS,
    postAccessArrangementUploadDocuments,
    {
      onSuccess: (res) => successCallback(res),
      onError: (err) => failCallback && failCallback(err),
    },
    false,
    false
  );

/** to post to get presignUrl
 * */
export const useGetAppealArrangementReviewDetails = (uuid: string, onSuccess: () => void) =>
  useCustomQuery<AccessArrangementGetReviewDetails>(
    GET_ACCESS_ARRANGEMENT_SELECTED_CONDITIONS,
    () => getReviewApplicationData(uuid).then((response) => response.data),
    { onSuccess }
  );

/** to put draft and save
 * */
export const usePutAppealArrangementReviewDetails = (
  onSuccess: (res: AxiosResponse) => void,
  onError?: (res: AxiosError<GenericErrorResponse>) => void
) =>
  useCustomMutate<AxiosResponse, UpdateAccessArrangementData, AxiosError<GenericErrorResponse>>(
    PUT_ACCESS_ARRANGEMENT_UPDATE_DRAFT,
    putReviewApplicationData,
    {
      onSuccess,
      onError,
    }
  );

/** to get Acknowledgement
 * */
export const useGetAppealArrangementRequestAcknowledgement = (
  uuid: string,
  successCallback?: () => void,
  errorCallback?: () => void
) =>
  useCustomQuery<AccessArrangementAcknowledgementResponse>(
    GET_ACCESS_ARRANGEMENT_SELECTED_CONDITIONS,
    () => getRequestAcknoledgment(uuid).then((response) => response.data),
    {
      onSuccess: successCallback,
      onError: errorCallback,
    }
  );

/** to get details for summary page
 * */
export const useGetAppealArrangementSummaryDetails = (uuid: string, enabled = true) =>
  useCustomQuery<AccessArrangmentGetSummaryDetails>(
    GET_ACCESS_ARRANGEMENT_SUMMARY,
    () => getRequestSummary(uuid).then((response) => response.data),
    {
      enabled,
    }
  );

/** to get pdf link for pdf summary
 * */
export const useGetAppealArrangementSummaryPDF = (
  uuid: string,
  onSuccess?: (data: AccessArrangmentGetSummaryPDF) => void,
  onError?: (data: AxiosError) => void
) =>
  useCustomQuery<AccessArrangmentGetSummaryPDF>(
    GET_ACCESS_ARRANGEMENT_DOWNLOAD_PDF,
    () => getSummaryPdf(uuid).then((response) => response.data),
    {
      enabled: false,
      onSuccess,
      onError,
    },
    true,
    false
  );

export const useGetMedicalConditionAmendment = (
  uuid: string,
  onSuccess: (res: MedicalConditionAmendment) => void
) =>
  useCustomQuery<MedicalConditionAmendment>(
    GET_MEDICAL_CONDITIONS_AMENDMENT,
    () => getMedicalConditionAmendment(uuid).then((response) => response.data),
    {
      enabled: false,
      onSuccess,
    }
  );

export const useGetAppealArrangementMedicalConditionsAmendment = (
  uuid: string,
  onSuccess: (res: AccessArrangmentGetMedicalConditionsResponse) => void
) =>
  useCustomQuery<AccessArrangmentGetMedicalConditionsResponse>(
    GET_ACCESS_ARRANGEMENT_MEDICAL_CONDITIONS_AMENDMENT,
    () => getAccessArrangementMedicalConditionAmendment(uuid).then((response) => response.data),
    { onSuccess }
  );

export const useGetReviewAmendmentApplicationData = (uuid: string, onSuccess: () => void) =>
  useCustomQuery<AccessArrangementGetReviewAmendment>(
    GET_ACCESS_ARRANGEMENT_REVIEW_DETAILS_AMENDMENT,
    () => getReviewAmendmentApplicationData(uuid).then((response) => response.data),
    { onSuccess }
  );

export const useGetIndividualReportFiles = (uuid: string) =>
  useCustomQuery<{ fileUuid: string; fileName: string; fileSizeInBytes: number }[]>(
    [GET_ACCESS_ARRANGEMENT_INDIVIDUAL_REPORT_FILES, uuid],
    () => getIndividualReportFiles(uuid).then((response) => response.data)
  );

export const useGetIndividualReportSubmittedFiles = (uuid: string) =>
  useCustomQuery<{ fileUuid: string; fileName: string; fileSizeInBytes: number }[]>(
    [GET_ACCESS_ARRANGEMENT_INDIVIDUAL_REPORT_SUBMITTED_FILES, uuid],
    () => getIndividualReportSubmittedFiles(uuid).then((response) => response.data)
  );

